/**
 * MediaStream.
 *
 * @copyright Realeyes OU. All rights reserved.
 */


import MediaTrackType from './MediaTrackType';
import ArgumentError from '../utils/ArgumentError';
import MediaStreamError from './MediaStreamError';


export default class MediaStream {
    /**
     * Creates a MediaStream wrapper for the plugin stream.
     *
     * @param {IMediaStream} pluginStream Original stream object from the plugin
     */
    constructor(pluginStream) {
        if (typeof pluginStream === 'undefined') {
            throw new ArgumentError('You should pass a valid MediaStream instance');
        }

        this.pluginStream = pluginStream;
        this.state = 'inactive';
    }

    /**
     * Stops media stream.
     *
     * @return {Promise.<boolean, MediaStreamError>}
     */
    stop() {
        if (this.state === 'disposed') {
            return Promise.reject(new MediaStreamError('Can not stop disposed recorder'));
        }

        return this.pluginStream.stop()
            .then(() => {
                this.state = 'inactive';

                return true;
            }, (err) => {
                throw new MediaStreamError(err);
            });
    }

    /**
     * Resolves with the array of stream tracks.
     *
     * @return {Promise.<Array.<IMediaStreamTrack>>}
     */
    getTracks() {
        return this.pluginStream.getTracks();
    }

    /**
     * Resolves with the video track.
     *
     * @return {Promise.<IVideoTrack>}
     */
    getVideoTrack() {
        return this.getTracks()
            .then(tracks => tracks.find(t => t.getType() === MediaTrackType.VIDEO));
    }

    /**
     * Resolves with the audio track.
     *
     * @return {Promise.<IAudioTrack>}
     */
    getAudioTrack() {
        return this.getTracks()
            .then(tracks => tracks.find(t => t.getType() === MediaTrackType.AUDIO));
    }

    /**
     * Disposes the stream.
     *
     * @return {Promise.<boolean, MediaStreamError>}
     */
    dispose() {
        return this.pluginStream.dispose()
            .then(() => {
                this.state = 'disposed';

                return true;
            }, (err) => {
                throw new MediaStreamError(err);
            });
    }

    /**
     * Returns the original plugin stream.
     *
     * @return {IMediaStream}
     */
    getPluginStream() {
        return this.pluginStream;
    }
}
